import { Route, Routes, Navigate } from 'react-router-dom';
import { LoginPage } from './features/auth/LoginPage';
import { AppShell } from './components/AppShell';
import { AdminShell } from './features/admin/AdminShell';
import { AnimatedBackground } from './components/AnimatedBackground';
import { AdminDashboardPage } from './features/admin/AdminDashboardPage';
import { AdminUsersPage } from './features/admin/AdminUsersPage';
import { AdminTeamsPage } from './features/admin/AdminTeamsPage';
import { AdminCompetenciesPage } from './features/admin/AdminCompetenciesPage';
import { AdminAuditLogPage } from './features/admin/AdminAuditLogPage';
import { IngestorsListPage } from './features/admin/IngestorsListPage';
import { IngestorNewPage } from './features/admin/IngestorNewPage';
import { IngestorDetailPage } from './features/admin/IngestorDetailPage';
import { IngestorActorsPage } from './features/admin/IngestorActorsPage';
import { ScoringRulesPage } from './features/admin/ScoringRulesPage';
import { StatusTransitionsPage } from './features/admin/StatusTransitionsPage';
import { IngestorTransitionsPage } from './features/admin/IngestorTransitionsPage';
import { EventTypesPage } from './features/admin/EventTypesPage';
import { BadgesPage } from './features/admin/BadgesPage';
import { DailyQuestTemplatesPage } from './features/admin/DailyQuestTemplatesPage';
import { PublicBoardSettingsPage } from './features/admin/PublicBoardSettingsPage';
import { TeamHeatmapPage } from './features/admin/TeamHeatmapPage';
import { TeamActivityPage } from './features/admin/TeamActivityPage';
import { CommanderPage } from './features/admin/CommanderPage';
import { CampaignHealthPage } from './features/admin/CampaignHealthPage';
import { PrFlowPage } from './features/admin/PrFlowPage';
import { ChroniclesAdminPage } from './features/admin/ChroniclesAdminPage';
import { ConditionTesterPage } from './features/admin/ConditionTesterPage';
import { QuestsListPage } from './features/admin/QuestsListPage';
import { QuestNewPage } from './features/admin/QuestNewPage';
import { QuestDetailPage } from './features/admin/QuestDetailPage';
import { AdventuresListPage } from './features/admin/adventures/AdventuresListPage';
import { AdventureNewPage } from './features/admin/adventures/AdventureNewPage';
import { AdventureDetailPage } from './features/admin/adventures/AdventureDetailPage';
import { BossesListPage } from './features/admin/bosses/BossesListPage';
import { BossNewPage } from './features/admin/bosses/BossNewPage';
import { BossDetailPage } from './features/admin/bosses/BossDetailPage';
import { AdminHelpPage } from './features/admin/help/AdminHelpPage';
import { PlayerDashboardPage } from './features/player/PlayerDashboardPage';
import { MySkillsPage } from './features/player/MySkillsPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { PublicBoardPage } from './features/public-board/PublicBoardPage';
import { BossFightsPage } from './features/boss-fights/BossFightsPage';
import { ChroniclesPage } from './features/chronicles/ChroniclesPage';
import { BossCardPrototypePage } from './features/prototype/BossCardPrototypePage';
import { BattlemapPage } from './features/admin/BattlemapPage';
import { PublicQuestBoardPage } from './features/quests/PublicQuestBoardPage';
import { TeamRewardDetailPage } from './features/coins/TeamRewardDetailPage';
import { TeamRewardsAdminPage } from './features/admin/teamRewards/TeamRewardsAdminPage';
import { RewardsAdminPage } from './features/admin/teamRewards/RewardsAdminPage';

export function App() {
  return (
    <>
      {/* Ambient background: a field of multicoloured
          dots drifting up the viewport. Pure CSS, GPU-
          accelerated, 60 dots total. Fixed-positioned
          at z-0 so it sits behind the app's content
          (AppShell is z-10+) but on top of the body's
          bg. The public board (/public-board) is now
          mounted OUTSIDE the AppShell (it's a full-
          screen wall TV — the AppShell chrome is
          irrelevant) and its root div is transparent
          so the dots show through the empty space
          around the rotating panels. The component
          self-honors prefers-reduced-motion by pausing
          the animation (dots render stationary, no
          layout shift). */}
      <AnimatedBackground />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/quests" element={<PublicQuestBoardPage />} />
      <Route path="/public-board" element={<PublicBoardPage />} />
      <Route path="/boss-fights" element={<BossFightsPage />} />
      {/* M5.14: The Chronicles — wall-TV surface for the
          per-team hourly fantasy prose. Bypasses AppShell
          (the operator has a dedicated monitor URL).
          Polls /api/public-board/chronicles every 30s.
          Animation is on by default; respects
          prefers-reduced-motion. */}
      <Route path="/chronicles" element={<ChroniclesPage />} />
      {/* v0.8.9 prototype: hidden route for the operator to
          review the new boss-card visual treatment before it
          ships to the public board. Not linked from any nav. */}
      <Route path="/prototype/boss-cards" element={<BossCardPrototypePage />} />
      {/* The battlemap lived at this URL while it was a prototype and
          people have it bookmarked. Forwarded rather than dropped. */}
      <Route path="/prototype/battlemap" element={<Navigate to="/admin/battlemap" replace />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<Navigate to="/player" replace />} />
        <Route path="/player" element={<PlayerDashboardPage />} />
        <Route path="/skills" element={<MySkillsPage />} />
        {/* v0.8.9: self-service profile edit. Display name, handle,
            pronouns, public-board opt-in. Role / ignore / password
            / email remain admin-only — see PATCH /api/me handler. */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin" element={<AdminShell />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="teams" element={<AdminTeamsPage />} />
          <Route path="ingestors" element={<IngestorsListPage />} />
          <Route path="ingestors/new" element={<IngestorNewPage />} />
          <Route path="ingestors/:id" element={<IngestorDetailPage />} />
          <Route path="ingestors/:id/actors" element={<IngestorActorsPage />} />
          <Route path="ingestors/:id/transitions" element={<IngestorTransitionsPage />} />
          <Route path="teams/:teamId/rewards" element={<TeamRewardsAdminPage />} />
          <Route path="rewards" element={<RewardsAdminPage />} />
          <Route path="scoring-rules" element={<ScoringRulesPage />} />
          <Route path="status-transitions" element={<StatusTransitionsPage />} />
          <Route path="event-types" element={<EventTypesPage />} />
          <Route path="badges" element={<BadgesPage />} />
          <Route path="daily-quests" element={<DailyQuestTemplatesPage />} />
          <Route path="public-board" element={<PublicBoardSettingsPage />} />
          <Route path="heatmap" element={<TeamHeatmapPage />} />
          <Route path="team-activity" element={<TeamActivityPage />} />
          <Route path="commander" element={<CommanderPage />} />
          <Route path="campaign-health" element={<CampaignHealthPage />} />
          <Route path="pr-flow" element={<PrFlowPage />} />
          {/* The sprint as a battlefield. An operator tool: it reads
              the active campaign, so it sits with the other surfaces
              that do rather than with the player pages. */}
          <Route path="battlemap" element={<BattlemapPage />} />
          <Route path="chronicles" element={<ChroniclesAdminPage />} />
          <Route path="quests" element={<QuestsListPage />} />
          <Route path="quests/new" element={<QuestNewPage />} />
          <Route path="quests/:id" element={<QuestDetailPage />} />
          <Route path="competencies" element={<AdminCompetenciesPage />} />
          <Route path="audit" element={<AdminAuditLogPage />} />
          <Route path="condition-tester" element={<ConditionTesterPage />} />
          <Route path="adventures" element={<AdventuresListPage />} />
          <Route path="adventures/new" element={<AdventureNewPage />} />
          <Route path="adventures/:id" element={<AdventureDetailPage />} />
          <Route path="bosses" element={<BossesListPage />} />
          <Route path="bosses/new" element={<BossNewPage />} />
          <Route path="bosses/:id" element={<BossDetailPage />} />
          <Route path="help" element={<AdminHelpPage />} />
        </Route>
        <Route path="/rewards/:id" element={<TeamRewardDetailPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/player" replace />} />
    </Routes>
    </>
  );
}
